// Aba "Visão geral" do detalhe do contrato — cards arrastáveis, mini-KPIs,
// linhas editáveis (Dropdown/MultiSelect/Email/Data) e painel de briefing.
// Extraído de OperacionalContratos.tsx no refactor de 2026-05-23 (Wave 2).

import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button, Input, Select, DatePicker, Checkbox, App as AntApp, Typography, Row, Col, Space, Modal, Form } from 'antd';
import { LinkOutlined, CopyOutlined, RobotOutlined, EditOutlined, HolderOutlined, PlusOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { API_URL } from '../../config';
import type { Contrato, UsuarioOp, ClienteSlim, ContatoClienteSlim } from './types';
import { fmt, num, fmtData, getToken, carregarUsuariosOp, carregarClientesArt, carregarContatosCliente } from './utils';
import { Card, Linha } from './shared-components';
import { ModalIncluirAssinado } from '../juridico-contratos/ModalIncluirAssinado';
import { abrirPdfDoProjeto } from '../juridico-contratos/api';
import { usePerms } from '../../hooks/usePerms';
import { CanalTeams } from './CanalTeams';
import { CardAreasCnae } from './CardAreasCnae';

// ─── VISÃO GERAL ───────────────────────────────────────────────
export function VisaoGeral(props: {
  contrato: Contrato; onAtualizar: () => void;
  linkPubAberto: boolean; setLinkPubAberto: (v: boolean) => void;
}) {
  return <AntApp><VisaoGeralInner {...props} /></AntApp>;
}

function VisaoGeralInner({ contrato, onAtualizar, linkPubAberto, setLinkPubAberto }: {
  contrato: Contrato; onAtualizar: () => void;
  linkPubAberto: boolean; setLinkPubAberto: (v: boolean) => void;
}) {
  const { message } = AntApp.useApp();
  const [linkInfo, setLinkInfo] = useState<{ hash: string } | null>(null);
  const [carregandoLink, setCarregandoLink] = useState(false);

  async function salvarCampo(campo: string, valor: any) {
    try {
      const token = await getToken();
      await axios.post(`${API_URL}/comercial/propostas-geradas`,
        { action: 'op_atualizar_contrato', id: contrato.id, [campo]: valor },
        { headers: { Authorization: token } });
      onAtualizar();
    } catch (e: any) {
      message.error('Erro: ' + (e.response?.data?.error || e.message));
      throw e;
    }
  }

  async function publicarLink() {
    setCarregandoLink(true);
    try {
      const token = await getToken();
      const r = await axios.post(`${API_URL}/comercial/propostas-geradas`,
        { action: 'op_publicar_cliente_link', cliente_id: contrato.cliente_id },
        { headers: { Authorization: token } });
      const data = r.data.body ? JSON.parse(r.data.body) : r.data;
      setLinkInfo({ hash: data.hash });
      setLinkPubAberto(true);
    } catch (e: any) {
      message.error('Erro: ' + (e.response?.data?.error || e.message));
    } finally { setCarregandoLink(false); }
  }

  const url = linkInfo
    ? `${window.location.origin}/publico/cliente/${linkInfo.hash}`
    : null;

  // ── Drag-and-drop dos cards: ordem persistida em localStorage ──
  // Card 'art' removido em 2026-05-18 (sem utilidade — info já aparece como
  // KPI no topo + aba ARTs dedicada com todas as ARTs do contrato).
  // 'areas' (2026-10-04): quanto do contrato é de cada área + CNAE do contrato.
  const ORDEM_PADRAO_VISAO = ['cliente', 'equipe', 'datas', 'financeiro', 'areas', 'acesso', 'herancas'];
  const [ordem, setOrdem] = useState<string[]>(() => {
    try {
      const salvo = localStorage.getItem('op:visaogeral:ordem');
      if (salvo) {
        const arr = JSON.parse(salvo);
        // Garante que TODOS os cards conhecidos aparecem (caso schema novo)
        const set = new Set(arr);
        ORDEM_PADRAO_VISAO.forEach(id => { if (!set.has(id)) arr.push(id); });
        return arr.filter((id: string) => ORDEM_PADRAO_VISAO.includes(id));
      }
    } catch {}
    return ORDEM_PADRAO_VISAO;
  });
  const [arrastando, setArrastando] = useState<string | null>(null);
  const [hoverAlvo, setHoverAlvo] = useState<string | null>(null);

  function moverCard(de: string, para: string) {
    if (de === para) return;
    setOrdem(curr => {
      const i = curr.indexOf(de), j = curr.indexOf(para);
      if (i < 0 || j < 0) return curr;
      const nv = [...curr];
      nv.splice(i, 1);
      nv.splice(j, 0, de);
      try { localStorage.setItem('op:visaogeral:ordem', JSON.stringify(nv)); } catch {}
      return nv;
    });
  }

  // Conteúdo de cada card mapeado por id
  const cards: Record<string, { titulo: string; conteudo: React.ReactNode }> = {
    cliente: {
      titulo: 'Cliente',
      conteudo: (() => {
        const partes: string[] = [];
        if (contrato.cliente_endereco) partes.push(contrato.cliente_endereco);
        if (contrato.cliente_bairro) partes.push(contrato.cliente_bairro);
        const cidadeUf = [contrato.cliente_cidade, contrato.cliente_uf].filter(Boolean).join('/');
        if (cidadeUf) partes.push(cidadeUf);
        if (contrato.cliente_cep) partes.push(`CEP ${contrato.cliente_cep}`);
        const enderecoCompleto = partes.length ? partes.join(' · ') : undefined;
        return <>
          <LinhaEditavelCliente
            rotulo="Razão social"
            valorAtualNome={contrato.cliente_nome}
            valorAtualId={contrato.cliente_id}
            podeEditarContrato={!!(contrato as any).pode_editar}
            onSalvar={(novoId) => salvarCampo('cliente_id', novoId)} />
          <Linha r="CNPJ" v={contrato.cliente_cnpj} />
          <Linha r="Endereço" v={enderecoCompleto} />
          <LinhaEditavelContato
            contrato={contrato}
            podeEditarContrato={!!(contrato as any).pode_editar}
            onSalvar={(novoId) => salvarCampo('contato_principal_id', novoId)}
            onAtualizarFull={onAtualizar} />
        </>;
      })(),
    },
    equipe: {
      titulo: 'Equipe',
      conteudo: <>
        <LinhaEditavelDropdown rotulo="Gestor do Contrato"
          valor={contrato.gestor_email}
          onSalvar={v => salvarCampo('gestor_email', v)} />
        <LinhaEditavelDropdown rotulo="Coordenador Técnico"
          valor={contrato.coordenador_email}
          onSalvar={v => salvarCampo('coordenador_email', v)} />
        <LinhaEditavelMultiSelect rotulo="Equipe técnica"
          valores={contrato.equipe_emails || (contrato.responsavel_email ? [contrato.responsavel_email] : [])}
          onSalvar={vs => salvarCampo('equipe_emails', vs)} />
      </>,
    },
    datas: {
      titulo: 'Datas',
      conteudo: <>
        {/* Datas previstas agora são editáveis (admin/gestor podem ajustar) —
            label muda quando preenchidas pra refletir origem se vieram da venda */}
        <LinhaEditavelData rotulo="Início previsto"
          valor={contrato.data_inicio_prevista}
          onSalvar={v => salvarCampo('data_inicio_prevista', v)} />
        <LinhaEditavelData rotulo="Término previsto"
          valor={contrato.data_termino_previsto}
          onSalvar={v => salvarCampo('data_termino_previsto', v)} />
        <LinhaEditavelData rotulo="Início real"
          valor={contrato.data_inicio_real}
          onSalvar={v => salvarCampo('data_inicio_real', v)} />
        <LinhaEditavelData rotulo="Término real"
          valor={contrato.data_termino_real}
          onSalvar={v => salvarCampo('data_termino_real', v)} />
      </>,
    },
    financeiro: {
      titulo: 'Financeiro',
      conteudo: (() => {
        const aditivos = num(contrato.aditivos_valor_soma);
        const base = num(contrato.valor_contratado);
        const total = base + aditivos;
        return <>
          <Linha r="Valor base (contrato)" v={base ? fmt(base) : '—'} />
          {aditivos !== 0 && (
            <Linha r="Aditivos"
              v={<span style={{ color: '#B07500' }}>
                {aditivos > 0 ? '+' : ''}{fmt(aditivos)}
                <span style={{ fontSize: 9, marginLeft: 6, color: '#888' }}>
                  ({contrato.aditivos_total || 0} aditivo{(contrato.aditivos_total || 0) !== 1 ? 's' : ''})
                </span>
              </span>} />
          )}
          <Linha r="Valor total"
            v={<strong style={{ color: '#020D7F', fontSize: 13 }}>{total ? fmt(total) : '—'}</strong>} />
          <Linha r="Forma de pagamento" v={contrato.forma_pagamento} />
        </>;
      })(),
    },
    areas: {
      titulo: 'Áreas e CNAE',
      conteudo: <CardAreasCnae contrato={contrato} onAtualizar={onAtualizar} />,
    },
    // Card 'art' removido — duplicava info que já aparece no KPI do topo
    // ("✅ ART vigente" / "📋 Sem ART") e na aba ARTs com detalhamento completo.
    acesso: {
      titulo: 'Acesso público do cliente',
      conteudo: !linkInfo && !linkPubAberto ? (
        <div>
          <Typography.Paragraph type="secondary" style={{ fontSize: 12, marginBottom: 10 }}>
            Gere um link público pra que o cliente acesse os documentos visíveis dos contratos dele.
            O link é único por cliente — todos os contratos dele aparecem em uma única tela.
          </Typography.Paragraph>
          <Button type="primary" icon={<LinkOutlined />}
            onClick={publicarLink} loading={carregandoLink}>
            Gerar link público
          </Button>
        </div>
      ) : (
        <div>
          <Typography.Paragraph type="secondary" style={{ fontSize: 12, marginBottom: 8 }}>
            Link único deste cliente (todos os contratos dele):
          </Typography.Paragraph>
          <Input.Group compact>
            <Input readOnly value={url || ''} style={{ width: 'calc(100% - 50px)', fontFamily: 'monospace' }}
              onClick={(e) => (e.target as HTMLInputElement).select()} />
            <Button icon={<CopyOutlined />}
              style={{ background: '#0a7f4a', borderColor: '#0a7f4a', color: '#fff' }}
              onClick={() => { if (url) { navigator.clipboard.writeText(url); message.success('Link copiado!'); }}} />
          </Input.Group>
        </div>
      ),
    },
    herancas: {
      titulo: 'Heranças do negócio',
      conteudo: <div style={{ fontSize: 11, color: '#666', lineHeight: 1.6 }}>
        Negócio de origem: <strong>{contrato.negocio_titulo || '—'}</strong><br />
        Documento de venda + anexos + camadas do mapa estão na aba <strong>Drive</strong> (pasta "comercial").
        As referências são compartilhadas — qualquer atualização no negócio se reflete aqui.
      </div>,
    },
  };

  return (
    <div>
      {/* Mini-cards de KPI no topo do detalhe — saúde do contrato em 1 olhada */}
      <MiniKpisContrato contrato={contrato} />

      <div style={{ fontSize: 10, color: '#888', marginBottom: 8, fontStyle: 'italic' }}>
        ↕ Arraste os cards pelo título pra reorganizar (a ordem é salva localmente).
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
        {ordem.map(id => {
          const c = cards[id];
          if (!c) return null;
          return (
            <CardArrastavel key={id} cardId={id} titulo={c.titulo}
              arrastando={arrastando === id}
              hoverAlvo={hoverAlvo === id}
              onDragStart={() => setArrastando(id)}
              onDragEnd={() => { setArrastando(null); setHoverAlvo(null); }}
              onDragOver={e => {
                e.preventDefault();
                if (arrastando && arrastando !== id) setHoverAlvo(id);
              }}
              onDragLeave={() => setHoverAlvo(curr => curr === id ? null : curr)}
              onDrop={e => {
                e.preventDefault();
                if (arrastando) moverCard(arrastando, id);
                setArrastando(null); setHoverAlvo(null);
              }}>
              {c.conteudo}
            </CardArrastavel>
          );
        })}
        <Card titulo="Pendências do kick-off" coluna2>
          <PendenciasKickoff contrato={contrato} />
        </Card>
        <CanalTeams key={contrato.id} contratoId={contrato.id} />
        <Card titulo="Contrato assinado" coluna2>
          <InstrumentoJuridico contrato={contrato} />
        </Card>
        <Card titulo="Briefing técnico" coluna2>
          <BriefingPainel contrato={contrato} onAtualizar={onAtualizar} />
        </Card>
      </div>
    </div>
  );
}

// ═══════════════════ PENDÊNCIAS DO KICK-OFF ══════════════════════════════
// KICKOFF (2026-08-20). O robô cobra por e-mail toda segunda, mas quem abre o
// contrato tem que ver o que falta AGORA — senão o gestor descobre a pendência
// só quando o e-mail chega, e o e-mail é o canal de quem esqueceu.
// Leitura pura (op_kickoff_pendencias): o mesmo levantamento que o job faz.
// 🚨 `venda_assinatura` e `instrumento_arquivado` SAÍRAM daqui (2026-09-07).
// As duas contam a mesma história e apareciam lado a lado, uma ✓ e a outra ✗:
// "Venda não formalizada ✓" junto de "Contrato assinado fora do acervo ✗".
// Verdadeiras as duas, ilegíveis juntas — foi o que o Lucas relatou.
// Viraram UMA linha, a do estado do papel, logo abaixo. A régua da COBRANÇA
// por e-mail não muda: continuam sendo dois tipos no `kickoffPendencias`.
const PENDENCIA_VIS: Record<string, { label: string; onde: string }> = {
  art:              { label: 'ART sem PDF emitido',      onde: 'aba ARTs' },
  coordenador:      { label: 'Sem coordenador técnico',  onde: 'aqui na Visão geral' },
  briefing:         { label: 'Briefing vazio',           onde: 'painel ao lado' },
  art_vencimento:   { label: 'Contrato vencendo sem ART emitida', onde: 'aba ARTs' },
};

/** O estado do papel, vindo pronto do servidor. Derivar da ausência de
 *  pendência mentia em três casos — contrato concluído (o levantamento vem
 *  vazio e o card pintava tudo de verde), venda formalizada por aceite no link
 *  (não há papel nenhum) e a diferença entre "sem documento" e "aguardando
 *  assinatura", que compartilham o mesmo detalhe. */
interface EstadoDoPapel {
  estado: 'sem_venda' | 'pendente' | 'aguardando' | 'aceite_sem_papel'
        | 'assinado_nao_arquivado' | 'papel_do_cliente' | 'arquivado';
  rotulo: string;
  venda_numero: string | null;
  codigo_no_acervo: string | null;
  pode_arquivar: boolean;
}

// Onde a pessoa resolve, por degrau. Cobrança sem próximo passo é só barulho.
const ONDE_RESOLVER: Record<EstadoDoPapel['estado'], string> = {
  sem_venda:              'gere o documento de venda na aba Vendas do negócio',
  pendente:               'envie para assinatura na aba Vendas do negócio',
  aguardando:             'o cliente ainda não assinou — acompanhe na aba Vendas',
  aceite_sem_papel:       'a venda foi aceita pelo link; o contrato assinado ainda não existe em lugar nenhum',
  assinado_nao_arquivado: 'assinado na D4 Sign — o papel ainda não subiu ao acervo',
  papel_do_cliente:       'o contrato do cliente chegou e ainda não subiu ao acervo',
  arquivado:              '',
};

// ═══════════════════ CONTRATO ASSINADO (instrumento jurídico) ════════════
// 2026-08-21. O contrato que o cliente assina é o documento que prova o que
// foi vendido — e vivia só no e-mail de quem negociou. Formalizar a venda
// (aceite no link, D4 Sign) e ARQUIVAR o papel são coisas diferentes: sem o
// PDF no acervo, ninguém acha o contrato depois, e a vigência não entra no
// aviso de segunda-feira.
//
// Aqui o gestor vê se o instrumento está guardado e anexa se não estiver. O
// PDF vai para o acervo da empresa e a ficha nasce ligada a ESTE projeto —
// o mesmo caminho do Jurídico, com o que o sistema já sabe preenchido.
function InstrumentoJuridico({ contrato }: { contrato: Contrato }) {
  const navigate = useNavigate();
  const { message } = AntApp.useApp();
  const [carregando, setCarregando] = useState(true);
  const [ficha, setFicha] = useState<any>(null);
  const [cobertura, setCobertura] = useState<any[]>([]);
  const [podeCriar, setPodeCriar] = useState(false);
  // O contrato existe, mas é de tipo sigiloso e esta pessoa não o vê.
  const [reservado, setReservado] = useState(false);
  // 'sem_modulo' e 'nao_vinculado' são coisas diferentes, e a mensagem antiga
  // ("seu acesso não inclui o módulo") passaria a mentir para metade de quem a lê.
  const [semAcesso, setSemAcesso] = useState<null | 'sem_modulo' | 'nao_vinculado'>(null);
  const [anexar, setAnexar] = useState(false);

  const carregar = React.useCallback(async () => {
    setCarregando(true);
    try {
      const token = await getToken();
      const r = await axios.post(`${API_URL}/comercial/propostas-geradas`,
        { action: 'jur_por_contrato', contrato_op_id: contrato.id },
        { headers: { Authorization: token } });
      const d = r.data.body ? JSON.parse(r.data.body) : r.data;
      setFicha(d.ficha || null);
      // GUARDA-CHUVA (2026-08-25): quando o papel cobre mais de um projeto, o
      // card diz quais — senão a pessoa acha que o contrato é só deste aqui e
      // anexa o mesmo PDF de novo no aditivo.
      setCobertura(d.cobertura || []);
      setPodeCriar(!!d.pode_criar);
      setReservado(!!d.reservado);
      setSemAcesso(null);
    } catch (e: any) {
      // 🚨 "Não posso ver" não é "não existe". Quem não tem o módulo Jurídico
      // não pode receber a mesma tela de quem tem e está sem o contrato
      // arquivado — sairia anexando um segundo PDF do que já está lá.
      setFicha(null); setCobertura([]); setPodeCriar(false); setReservado(false);
      const msg = String(e?.response?.data?.error || '');
      setSemAcesso(e?.response?.status !== 403 ? null
        : (/equipe deste projeto/i.test(msg) ? 'nao_vinculado' : 'sem_modulo'));
    } finally { setCarregando(false); }
  }, [contrato.id]);

  useEffect(() => { carregar(); }, [carregar]);

  if (carregando) return <Typography.Text type="secondary" style={{ fontSize: 12 }}>carregando…</Typography.Text>;
  if (semAcesso) return (
    <Typography.Text type="secondary" style={{ fontSize: 12 }}>
      {semAcesso === 'nao_vinculado'
        ? 'Você não está na equipe deste projeto. Quem é gestor, coordenador, responsável '
          + 'ou está na equipe consegue ver o contrato assinado aqui — peça ao gestor para '
          + 'te incluir.'
        : 'O acervo de contratos é do módulo Jurídico, e seu acesso não inclui ele. Quem '
          + 'responde pelo contrato consegue ver e anexar por lá.'}
    </Typography.Text>
  );

  // 🚨 Sigiloso ≠ ausente: com a mensagem de "ainda não está no acervo", a
  // pessoa anexaria um segundo PDF do contrato que já está lá.
  if (reservado) return (
    <Typography.Text type="secondary" style={{ fontSize: 12 }}>
      O contrato assinado deste projeto está no acervo, mas é de um tipo reservado (tem dado
      pessoal). Quem participa dele ou o arquivou consegue ver no Jurídico.
    </Typography.Text>
  );

  return (
    <>
      {ficha ? (
        <Space direction="vertical" size={2} style={{ width: '100%' }}>
          <Linha r="No acervo" v={`${ficha.codigo || 'documento'} · ${ficha.tipo_nome || 'contrato'}`} />
          <Linha r="Contraparte" v={ficha.contraparte_nome || '—'} />
          <Linha r="Vigência" v={ficha.vigencia_fim ? fmtData(ficha.vigencia_fim)
            : 'sem prazo cadastrado — abra a ficha e complete'} />
          <Linha r="Situação" v={ficha.situacao_calc || ficha.situacao} />
          {cobertura.length > 1 && (
            <Linha r="Cobre" v={cobertura.map((x: any) => x.numero).join(' · ')} />
          )}
          <Space style={{ marginTop: 6 }}>
            {ficha.documento_id && (
              <Button size="small" type="primary" onClick={async () => {
                try {
                  // Porta do PROJETO: a do acervo responde 404 para a
                  // categoria restrita, e quem acabou de ganhar o card não
                  // abriria nada.
                  const url = await abrirPdfDoProjeto(contrato.id);
                  window.open(url, '_blank', 'noopener');
                } catch (e: any) {
                  message.error('Não consegui abrir o PDF: '
                    + (e.response?.data?.error || e.message));
                }
              }}>Abrir PDF</Button>
            )}
            <Button size="small" onClick={() => navigate('/juridico/contratos')}>
              Ver no Jurídico
            </Button>
          </Space>
        </Space>
      ) : (
        <Space direction="vertical" size={6} style={{ width: '100%' }}>
          <Typography.Text type="secondary" style={{ fontSize: 12 }}>
            O contrato assinado deste projeto ainda não está no acervo. Enquanto ele não
            estiver, o documento vive no e-mail de quem negociou e a vigência não entra no
            aviso de segunda-feira.
          </Typography.Text>
          {podeCriar && (
            <Button type="primary" size="small" onClick={() => setAnexar(true)}>
              Anexar contrato assinado
            </Button>
          )}
        </Space>
      )}
      <ModalIncluirAssinado
        aberto={anexar}
        onFechar={() => setAnexar(false)}
        onArquivado={carregar}
        venda={{
          contrato_op_id: contrato.id,
          numero: contrato.numero,
          titulo: contrato.titulo,
          cliente_id: contrato.cliente_id,
          cliente_nome: contrato.cliente_nome,
          valor_contratado: contrato.valor_contratado,
        }}
      />
    </>
  );
}

function PendenciasKickoff({ contrato }: { contrato: Contrato }) {
  const { message } = AntApp.useApp();
  const [itens, setItens] = useState<Array<{ tipo: string; detalhe: string | null }> | null>(null);
  const [papel, setPapel] = useState<EstadoDoPapel | null>(null);
  const [erro, setErro] = useState(false);
  const [arquivando, setArquivando] = useState(false);

  const carregar = React.useCallback(async () => {
    try {
      const token = await getToken();
      const r = await axios.post(`${API_URL}/comercial/propostas-geradas`,
        { action: 'op_kickoff_pendencias', contrato_id: contrato.id },
        { headers: { Authorization: token } });
      const d = r.data.body ? JSON.parse(r.data.body) : r.data;
      setItens(d?.pendencias || []);
      setPapel(d?.papel || null);
    } catch { setErro(true); }
  }, [contrato.id]);

  useEffect(() => { carregar(); }, [carregar]);

  // "Arquivar agora": o mesmo trabalho que o robô faz sozinho, na hora, em vez
  // de esperar a segunda-feira. Só aparece quando EXISTE papel para arquivar —
  // com aceite pelo link, por exemplo, ele responderia sucesso sem fazer nada.
  async function arquivarAgora() {
    setArquivando(true);
    try {
      const token = await getToken();
      const r = await axios.post(`${API_URL}/comercial/propostas-geradas`,
        { action: 'jur_arquivar_agora', contrato_op_id: contrato.id },
        { headers: { Authorization: token } });
      const d = r.data.body ? JSON.parse(r.data.body) : r.data;
      if (d?.ok === false) message.warning(d.motivo || 'Não consegui arquivar agora.');
      else message.success('Contrato arquivado no acervo.');
      await carregar();
    } catch (e: any) {
      message.error('Não consegui arquivar: '
        + (e.response?.data?.error || e.message));
    } finally { setArquivando(false); }
  }

  if (erro) return <div style={{ fontSize: 12, color: '#888' }}>Não foi possível checar as pendências.</div>;
  if (itens === null) return <div style={{ fontSize: 12, color: '#888' }}>Checando…</div>;

  const pendentes = new Set(itens.map(i => i.tipo));
  // ART × vencimento (2026-08-25): o robô SUBSTITUI a cobrança genérica de ART
  // pela de vencimento quando o término está perto — no card, isso é UMA linha
  // de ART cujo rótulo escala, não duas (senão apareceria "✓ ART sem PDF" ao
  // lado de "✗ vencendo sem ART", uma contradição).
  const artVencendo = pendentes.has('art_vencimento');
  const linhas = Object.entries(PENDENCIA_VIS)
    .filter(([tipo]) => tipo !== 'art_vencimento')
    .map(([tipo, visBase]) => {
    const vis = tipo === 'art' && artVencendo ? PENDENCIA_VIS.art_vencimento : visBase;
    const pend = pendentes.has(tipo) || (tipo === 'art' && artVencendo);
    const detalhe = itens.find(i => i.tipo === (tipo === 'art' && artVencendo
      ? 'art_vencimento' : tipo))?.detalhe;
    return (
      <div key={tipo} style={{ display: 'flex', alignItems: 'flex-start', gap: 8,
        padding: '5px 0', fontSize: 12, borderBottom: '0.5px solid #f5f5f5' }}>
        <span style={{ color: pend ? '#B91626' : '#0a7f4a', fontWeight: 700, lineHeight: '18px' }}>
          {pend ? '✗' : '✓'}
        </span>
        <span style={{ flex: 1, color: pend ? '#222' : '#888' }}>
          {vis.label}
          {pend && <span style={{ color: '#888' }}> — resolva na {vis.onde}
            {detalhe ? ` (${detalhe})` : ''}</span>}
        </span>
      </div>
    );
  });

  // A UMA linha do papel, escalonada. Substitui as duas que se contradiziam.
  const papelOk = papel?.estado === 'arquivado';
  const abertas = pendentes.size + (papel && !papelOk ? 1 : 0);

  const linhaPapel = papel && (
    <div key="papel" style={{ display: 'flex', alignItems: 'flex-start', gap: 8,
      padding: '5px 0', fontSize: 12, borderBottom: '0.5px solid #f5f5f5' }}>
      <span style={{ color: papelOk ? '#0a7f4a' : '#B91626', fontWeight: 700, lineHeight: '18px' }}>
        {papelOk ? '✓' : '✗'}
      </span>
      <span style={{ flex: 1, color: papelOk ? '#888' : '#222' }}>
        {papel.rotulo}
        {papel.venda_numero && <span style={{ color: '#888' }}> · {papel.venda_numero}</span>}
        {papelOk && papel.codigo_no_acervo && (
          <span style={{ color: '#888' }}> ({papel.codigo_no_acervo})</span>
        )}
        {!papelOk && ONDE_RESOLVER[papel.estado] && (
          <span style={{ color: '#888' }}> — {ONDE_RESOLVER[papel.estado]}</span>
        )}
        {papel.pode_arquivar && (
          <Button size="small" type="link" loading={arquivando}
            style={{ padding: '0 6px', height: 18, fontSize: 11 }}
            onClick={arquivarAgora}>
            arquivar agora
          </Button>
        )}
      </span>
    </div>
  );

  return (
    <div>
      <div style={{ fontSize: 12, marginBottom: 6,
        color: abertas ? '#B07500' : '#0a7f4a', fontWeight: 600 }}>
        {abertas
          ? `${abertas} pendência(s) — cobrança por e-mail toda segunda`
          : 'Kick-off completo'}
      </div>
      {linhaPapel}
      {linhas}
    </div>
  );
}

// ════════════════ MINI-CARDS DE KPI (DETALHE DO CONTRATO) ════════════════
// Painel compacto no topo do detalhe — saúde do contrato em 1 olhada.
// Usa apenas dados que já vêm em contrato (sem fetch extra).
function MiniKpisContrato({ contrato }: { contrato: Contrato }) {
  const hoje = new Date(); hoje.setHours(0, 0, 0, 0);
  // Aceita só YYYY-MM-DD (10 chars). Se vier vazio, undefined, com formato
  // inesperado (ex.: ISO completo com T) ou for inválido, fica null em vez
  // de virar NaN no cálculo de dias (antes renderizava "NaNd" no card).
  const dtPrevSrc = (contrato.data_termino_previsto || '').toString().slice(0, 10);
  const fimPrevisto = /^\d{4}-\d{2}-\d{2}$/.test(dtPrevSrc)
    ? new Date(dtPrevSrc + 'T12:00:00') : null;
  const fimMs = fimPrevisto?.getTime();
  const diasRestantes = (fimMs != null && Number.isFinite(fimMs))
    ? Math.round((fimMs - hoje.getTime()) / 86400000)
    : null;
  const concluido = contrato.status === 'concluido';
  const cancelado = contrato.status === 'cancelado';

  // Atividades atrasadas: status != concluida e prev_termino < hoje (calculo no front)
  const ativsAtrasadas = (contrato as any).atividades_atrasadas
    ?? null;  // não está no contrato — mantém placeholder
  const totAtivs = contrato.atividades_total || 0;
  const concAtivs = contrato.atividades_concluidas || 0;

  // Saldo a faturar (parcelas)
  const saldoAberto = contrato.parcelas_valor_aberto || 0;

  // Entregáveis em risco: data_prevista < hoje e status != entregue
  const entrsRisco = (contrato.entregaveis || []).filter(e => {
    if (e.status === 'entregue' || e.status === 'medido' || e.status === 'faturado') return false;
    if (!e.data_prevista) return false;
    return new Date(e.data_prevista + 'T12:00:00') < hoje;
  }).length;
  const entrsTotais = contrato.entregaveis_total || 0;

  // ART vigente?
  const temArtVigente = (contrato.arts || []).some(a => {
    if (!a.fim_art) return !!a.numero;
    return new Date(a.fim_art + 'T12:00:00') >= hoje;
  });

  const cards: Array<{ key: string; emoji: string; label: string;
    valor: React.ReactNode; cor: string; bg: string; alerta?: boolean }> = [
    {
      key: 'prazo',
      emoji: cancelado ? '⏸' : concluido ? '✅' : (diasRestantes != null && diasRestantes < 0 ? '🚨' : diasRestantes != null && diasRestantes <= 7 ? '⏰' : '📅'),
      label: cancelado ? 'Status' : concluido ? 'Concluído em' : 'Prazo',
      valor: cancelado ? 'Cancelado'
           : concluido ? (contrato.data_termino_real ? fmtData(contrato.data_termino_real) : '—')
           : diasRestantes == null ? '—'
           : diasRestantes < 0 ? `${Math.abs(diasRestantes)}d atraso`
           : `${diasRestantes}d`,
      cor: cancelado ? '#888' : concluido ? '#0a7f4a'
         : diasRestantes != null && diasRestantes < 0 ? '#B91626'
         : diasRestantes != null && diasRestantes <= 7 ? '#B07500'
         : '#0930D0',
      bg: cancelado ? '#F2F2F4' : concluido ? '#E8F5EE'
        : diasRestantes != null && diasRestantes < 0 ? '#FDECEC'
        : diasRestantes != null && diasRestantes <= 7 ? '#FFF8E8'
        : '#EFF3FF',
      alerta: !concluido && !cancelado && diasRestantes != null && diasRestantes < 0,
    },
    {
      key: 'atividades',
      emoji: '✅',
      label: 'Atividades',
      valor: totAtivs > 0 ? `${concAtivs}/${totAtivs}` : '—',
      cor: '#0a7f4a', bg: '#E8F5EE',
    },
    ...(ativsAtrasadas != null ? [{
      key: 'ativs_atrasadas',
      emoji: '🔴',
      label: 'Atrasadas',
      valor: ativsAtrasadas,
      cor: '#B91626', bg: '#FDECEC',
      alerta: ativsAtrasadas > 0,
    }] : []),
    ...(entrsTotais > 0 ? [{
      key: 'entregaveis',
      emoji: entrsRisco > 0 ? '⚠' : '📦',
      label: 'Entregáveis em risco',
      valor: entrsRisco > 0 ? `${entrsRisco}/${entrsTotais}` : `0/${entrsTotais}`,
      cor: entrsRisco > 0 ? '#B07500' : '#666',
      bg: entrsRisco > 0 ? '#FFF8E8' : '#F2F2F4',
      alerta: entrsRisco > 0,
    }] : []),
    {
      key: 'saldo',
      emoji: '💰',
      label: 'Saldo a faturar',
      valor: saldoAberto > 0 ? fmt(saldoAberto) : '—',
      cor: '#0a7f4a', bg: '#E8F5EE',
    },
    {
      key: 'art',
      emoji: temArtVigente ? '✅' : '📋',
      label: 'ART',
      valor: temArtVigente
        ? `${(contrato.arts || []).length} vigente${(contrato.arts || []).length === 1 ? '' : 's'}`
        : (contrato.arts && contrato.arts.length > 0 ? 'Vencida' : 'Sem ART'),
      cor: temArtVigente ? '#0a7f4a' : '#B07500',
      bg: temArtVigente ? '#E8F5EE' : '#FFF8E8',
      alerta: !temArtVigente && !cancelado,
    },
  ];

  return (
    <Row gutter={[8, 8]} style={{ marginBottom: 14 }}>
      {cards.map(c => (
        <Col key={c.key} xs={12} sm={8} md={6} lg={4}>
          <div style={{ padding: '10px 12px',
            background: c.bg, border: c.alerta ? `1px solid ${c.cor}` : '0.5px solid #e0e0e0',
            borderRadius: 5, color: c.cor, height: '100%' }}>
            <Typography.Text strong style={{ fontSize: 9, color: c.cor,
              textTransform: 'uppercase', letterSpacing: 0.4, display: 'block', marginBottom: 4 }}>
              <span style={{ fontSize: 12 }}>{c.emoji}</span> {c.label}
            </Typography.Text>
            <div style={{ fontSize: 17, fontWeight: 700, lineHeight: 1.1, color: '#222' }}>
              {c.valor}
            </div>
          </div>
        </Col>
      ))}
    </Row>
  );
}

// Card com handle de drag-and-drop pra reordenar dentro da Visão Geral.
// O `draggable` está só no header (não no card todo) pra que o conteúdo
// (inputs, dropdowns, links) continue funcionando normalmente.
function CardArrastavel({ cardId, titulo, children, arrastando, hoverAlvo,
  onDragStart, onDragEnd, onDragOver, onDragLeave, onDrop }: {
  cardId: string; titulo: string; children: React.ReactNode;
  arrastando: boolean; hoverAlvo: boolean;
  onDragStart: () => void; onDragEnd: () => void;
  onDragOver: (e: React.DragEvent) => void;
  onDragLeave: () => void;
  onDrop: (e: React.DragEvent) => void;
}) {
  return (
    <div
      onDragOver={onDragOver} onDragLeave={onDragLeave} onDrop={onDrop}
      style={{
        background: '#fff', padding: 14, borderRadius: 6,
        border: hoverAlvo ? '1.5px dashed #0930D0' : '0.5px solid #e0e0e0',
        opacity: arrastando ? 0.4 : 1,
        transition: 'opacity 0.15s, border-color 0.15s',
        position: 'relative',
      }}>
      <div
        draggable
        onDragStart={(e) => {
          e.dataTransfer.effectAllowed = 'move';
          e.dataTransfer.setData('text/plain', cardId);
          onDragStart();
        }}
        onDragEnd={onDragEnd}
        title="Arraste pra reordenar"
        style={{
          margin: '0 0 10px',
          cursor: 'grab', userSelect: 'none',
          display: 'flex', alignItems: 'center', gap: 6,
        }}>
        <HolderOutlined style={{ color: '#bbb' }} />
        <Typography.Text strong style={{
          fontSize: 12, color: '#020D7F',
          textTransform: 'uppercase', letterSpacing: 0.5,
        }}>{titulo}</Typography.Text>
      </div>
      {children}
    </div>
  );
}

// Dropdown editável de email (singular). Lista usuários do grupo 'operacional'.
function LinhaEditavelDropdown({ rotulo, valor, onSalvar }: {
  rotulo: string; valor?: string | null; onSalvar: (v: string | null) => Promise<any>;
}) {
  const [editando, setEditando] = useState(false);
  const [v, setV] = useState(valor || '');
  const [salvando, setSalvando] = useState(false);
  const [usuarios, setUsuarios] = useState<UsuarioOp[]>([]);
  useEffect(() => { setV(valor || ''); }, [valor]);
  useEffect(() => { if (editando) carregarUsuariosOp().then(setUsuarios); }, [editando]);

  async function salvar(novoValor: string) {
    if (novoValor === (valor || '')) { setEditando(false); return; }
    setSalvando(true);
    try {
      await onSalvar(novoValor || null);
      setV(novoValor);
      setEditando(false);
    } catch { setV(valor || ''); }
    finally { setSalvando(false); }
  }

  // Mostra nome do usuário se conhecido
  const nomeExibido = (() => {
    if (!valor) return null;
    const u = usuarios.find(x => x.email.toLowerCase() === valor.toLowerCase());
    return u ? `${u.nome} (${valor})` : valor;
  })();

  return (
    <div style={{ display: 'grid', gridTemplateColumns: '160px 1fr 24px', gap: 6,
      padding: '4px 0', fontSize: 12, color: '#222', alignItems: 'center' }}>
      <span style={{ color: '#888' }}>{rotulo}</span>
      {editando ? (
        <Select autoFocus disabled={salvando}
          value={v || undefined}
          onChange={(novo) => salvar(novo || '')}
          onBlur={() => setEditando(false)}
          placeholder="— sem definição —"
          allowClear showSearch optionFilterProp="label" size="small"
          style={{ width: '100%' }}
          options={usuarios.map(u => ({ value: u.email, label: `${u.nome} (${u.email})` }))} />
      ) : (
        <Typography.Text style={{ color: valor ? '#222' : '#bbb' }}>
          {nomeExibido || '— a definir'}
        </Typography.Text>
      )}
      {!editando && (
        <Button type="text" size="small" icon={<EditOutlined />}
          onClick={() => setEditando(true)} title="Editar equipe"
          style={{ color: '#0930D0' }} />
      )}
    </div>
  );
}

// Dropdown editável de cliente (singular). Lista clientes do banco.
// Aparece o botao editar se quem ve tem direito de editar o contrato em si
// (admin/diretor com perm OU gestor vinculado em contrato_equipe). Backend
// faz check final em opAtualizarContrato.
function LinhaEditavelCliente({ rotulo, valorAtualNome, valorAtualId, podeEditarContrato, onSalvar }: {
  rotulo: string;
  valorAtualNome?: string | null;
  valorAtualId?: number | null;
  podeEditarContrato: boolean;
  onSalvar: (novoId: number | null) => Promise<any>;
}) {
  const { pode } = usePerms();
  // Mostra ✏ pra qualquer um que pode editar o contrato — gestor vinculado
  // ou admin/diretor com perm. Backend reprova se nao for um dos 2.
  const podeEditar = podeEditarContrato || pode('operacional.contrato.editar.cliente');
  const [editando, setEditando] = useState(false);
  const [v, setV] = useState<number | null>(valorAtualId ?? null);
  const [salvando, setSalvando] = useState(false);
  const [clientes, setClientes] = useState<ClienteSlim[]>([]);
  useEffect(() => { setV(valorAtualId ?? null); }, [valorAtualId]);
  useEffect(() => { if (editando) carregarClientesArt().then(setClientes); }, [editando]);

  async function salvar(novoId: number | null) {
    if (novoId === (valorAtualId ?? null)) { setEditando(false); return; }
    setSalvando(true);
    try {
      await onSalvar(novoId);
      setV(novoId);
      setEditando(false);
    } catch { setV(valorAtualId ?? null); }
    finally { setSalvando(false); }
  }

  return (
    <div style={{ display: 'grid', gridTemplateColumns: '160px 1fr 24px', gap: 6,
      padding: '4px 0', fontSize: 12, color: '#222', alignItems: 'center' }}>
      <span style={{ color: '#888' }}>{rotulo}</span>
      {editando ? (
        <Select autoFocus disabled={salvando}
          value={v ?? undefined}
          onChange={(novo) => salvar(novo == null ? null : Number(novo))}
          onBlur={() => setEditando(false)}
          placeholder="Digite razao, fantasia ou CNPJ…"
          allowClear showSearch size="small"
          style={{ width: '100%' }}
          // Filtro rico: razao_social + nome_fantasia + CNPJ (digits-only).
          // optionFilterProp padrão só cobre a label visivel (que tem só razao+CNPJ
          // formatado); este custom permite buscar pelo fantasia tambem e por
          // pedaco de CNPJ sem precisar digitar pontuacao.
          filterOption={(input, opt) => {
            const q = (input || '').toLowerCase().trim();
            if (!q) return true;
            const alvo = ((opt as any)?._busca || '');
            if (alvo.includes(q)) return true;
            // Pedaço de CNPJ: extrai digits do input e procura no alvo (que tem cnpj digits)
            const qDigits = q.replace(/\D/g, '');
            return qDigits.length >= 3 && alvo.includes(qDigits);
          }}
          options={clientes.map(c => ({
            value: c.id,
            label: c.razao_social
              + (c.nome_fantasia ? ` · ${c.nome_fantasia}` : '')
              + (c.cnpj ? ` · CNPJ ${c.cnpj}` : ''),
            _busca: [
              c.razao_social,
              c.nome_fantasia,
              (c.cnpj || '').replace(/\D/g, ''),
            ].filter(Boolean).join(' ').toLowerCase(),
          }))} />
      ) : (
        <Typography.Text style={{ color: valorAtualNome ? '#222' : '#bbb' }}>
          {valorAtualNome || '— sem cliente'}
        </Typography.Text>
      )}
      {!editando && podeEditar && (
        <Button type="text" size="small" icon={<EditOutlined />}
          onClick={() => setEditando(true)} title="Trocar cliente"
          style={{ color: '#0930D0' }} />
      )}
    </div>
  );
}

// Linha editavel de "Contato principal" do contrato. Mostra nome + email + telefone
// (sub-linha). Botao ✏ abre Select com contatos vinculados ao cliente atual + opcao
// "➕ Novo contato" no rodape do dropdown — abre Modal que cria + auto-associa via
// junction comercial.cliente_contato + ja seta como contato principal do contrato.
function LinhaEditavelContato({ contrato, podeEditarContrato, onSalvar, onAtualizarFull }: {
  contrato: Contrato;
  podeEditarContrato: boolean;
  onSalvar: (novoId: number | null) => Promise<any>;
  onAtualizarFull: () => void;
}) {
  // Gate de edição = mesmo do contrato genérico. Trocar contato nao tem impacto
  // financeiro como trocar cliente, entao basta podeEditarContrato (matriz +
  // contrato_equipe). Backend usa CAMPOS_CONTRATO genérico (sem perm extra).
  const podeEditar = podeEditarContrato;
  // Cadastrar contato NOVO é do cadastro do Comercial (servidor recusa sem a
  // permissão desde 05/10/2026); escolher um já cadastrado segue sendo do contrato.
  const { pode, erro: permsErro } = usePerms();
  const podeCriarContato = permsErro || pode('comercial.contatos.criar');
  const [editando, setEditando] = useState(false);
  const [v, setV] = useState<number | null>(contrato.contato_principal_id ?? null);
  const [contatos, setContatos] = useState<ContatoClienteSlim[]>([]);
  const [salvando, setSalvando] = useState(false);
  const [modalAberto, setModalAberto] = useState(false);

  useEffect(() => { setV(contrato.contato_principal_id ?? null); }, [contrato.contato_principal_id]);
  useEffect(() => {
    if (editando && contrato.cliente_id) {
      carregarContatosCliente(contrato.cliente_id).then(setContatos);
    }
  }, [editando, contrato.cliente_id]);

  async function salvar(novoId: number | null) {
    if (novoId === (contrato.contato_principal_id ?? null)) { setEditando(false); return; }
    setSalvando(true);
    try {
      await onSalvar(novoId);
      setV(novoId);
      setEditando(false);
    } catch { setV(contrato.contato_principal_id ?? null); }
    finally { setSalvando(false); }
  }

  // Display sub-linha (email · telefone), só quando tem algum
  const detalhes: string[] = [];
  if (contrato.contato_email) detalhes.push(contrato.contato_email);
  if (contrato.contato_telefone) detalhes.push(contrato.contato_telefone);

  return (
    <>
      <div style={{ display: 'grid', gridTemplateColumns: '160px 1fr 24px', gap: 6,
        padding: '4px 0', fontSize: 12, color: '#222', alignItems: 'flex-start' }}>
        <span style={{ color: '#888', paddingTop: 2 }}>Contato</span>
        {editando ? (
          <Select autoFocus disabled={salvando}
            value={v ?? undefined}
            onChange={(novo) => salvar(novo == null ? null : Number(novo))}
            onBlur={() => setEditando(false)}
            placeholder={contrato.cliente_id ? 'Selecione um contato…' : 'Defina o cliente primeiro'}
            allowClear showSearch size="small" style={{ width: '100%' }}
            notFoundContent={contrato.cliente_id ? 'Nenhum contato cadastrado neste cliente' : 'Sem cliente'}
            filterOption={(input, opt) => {
              const q = (input || '').toLowerCase().trim();
              if (!q) return true;
              return ((opt as any)?._busca || '').includes(q);
            }}
            dropdownRender={(menu) => (
              <>
                {menu}
                {contrato.cliente_id && podeCriarContato && (
                  <div style={{ borderTop: '1px solid #eee', padding: 4 }}>
                    <Button type="link" size="small" icon={<PlusOutlined />}
                      // onMouseDown.preventDefault evita o onBlur do Select fechar
                      // o dropdown ANTES do onClick do botao disparar.
                      onMouseDown={(e) => e.preventDefault()}
                      onClick={() => { setEditando(false); setModalAberto(true); }}>
                      Novo contato pra este cliente
                    </Button>
                  </div>
                )}
              </>
            )}
            options={contatos.map(c => ({
              value: c.id,
              label: c.nome
                + (c.cargo ? ` · ${c.cargo}` : '')
                + (c.principal ? ' ★' : '')
                + (c.email ? ` · ${c.email}` : ''),
              _busca: [c.nome, c.cargo, c.email, c.telefone].filter(Boolean).join(' ').toLowerCase(),
            }))} />
        ) : (
          <div>
            <Typography.Text style={{ color: contrato.contato_nome ? '#222' : '#bbb' }}>
              {contrato.contato_nome || '— sem contato'}
            </Typography.Text>
            {detalhes.length > 0 && (
              <div style={{ fontSize: 11, color: '#666', marginTop: 2 }}>
                {detalhes.join(' · ')}
              </div>
            )}
          </div>
        )}
        {!editando && podeEditar && (
          <Button type="text" size="small" icon={<EditOutlined />}
            onClick={() => setEditando(true)} title="Trocar/criar contato"
            style={{ color: '#0930D0' }} />
        )}
      </div>
      {modalAberto && contrato.cliente_id && (
        <ModalCriarContato
          clienteId={contrato.cliente_id}
          clienteNome={contrato.cliente_nome || `Cliente #${contrato.cliente_id}`}
          onClose={() => setModalAberto(false)}
          onCriado={async (novo) => {
            setModalAberto(false);
            // Define o contato recem-criado como contato principal do contrato
            try {
              await onSalvar(novo.id);
            } finally {
              onAtualizarFull();
            }
          }} />
      )}
    </>
  );
}

// Modal pequeno pra criar contato vinculado a UM cliente especifico (cliente_id
// fixo, sem dropdown de empresa). Backend POST /comercial/contatos com body.cliente_id
// ja cria + auto-associa via junction comercial.cliente_contato.
function ModalCriarContato({ clienteId, clienteNome, onClose, onCriado }: {
  clienteId: number;
  clienteNome: string;
  onClose: () => void;
  onCriado: (contato: any) => void;
}) {
  const { message } = AntApp.useApp();
  const [form, setForm] = useState({ nome: '', email: '', telefone: '', cargo: '', decisor: false });
  const [salvando, setSalvando] = useState(false);

  async function salvar() {
    if (!form.nome.trim()) { message.warning('Nome obrigatório'); return; }
    setSalvando(true);
    try {
      const token = await getToken();
      const res = await axios.post(`${API_URL}/comercial/contatos`,
        { ...form, cliente_id: clienteId, principal: form.decisor },
        { headers: { Authorization: token } });
      const data = res.data?.body ? JSON.parse(res.data.body) : res.data;
      message.success('Contato criado e vinculado.');
      onCriado(data);
    } catch (e: any) {
      message.error('Erro: ' + (e.response?.data?.error || e.response?.data?.erro || e.message));
    } finally { setSalvando(false); }
  }

  return (
    <Modal open onCancel={onClose} onOk={salvar} confirmLoading={salvando}
      title={`Novo contato — ${clienteNome}`} okText="Criar e usar" cancelText="Cancelar"
      width={460} destroyOnClose>
      <Form layout="vertical" size="small">
        <Form.Item label="Nome" required>
          <Input value={form.nome} autoFocus
            onChange={e => setForm({ ...form, nome: e.target.value })} />
        </Form.Item>
        <Row gutter={10}>
          <Col span={12}>
            <Form.Item label="E-mail">
              <Input type="email" value={form.email}
                onChange={e => setForm({ ...form, email: e.target.value })} />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item label="Telefone">
              <Input value={form.telefone}
                onChange={e => setForm({ ...form, telefone: e.target.value })} />
            </Form.Item>
          </Col>
        </Row>
        <Form.Item label="Cargo">
          <Input value={form.cargo}
            onChange={e => setForm({ ...form, cargo: e.target.value })}
            placeholder="Ex.: Gerente de Engenharia" />
        </Form.Item>
        <Checkbox checked={form.decisor}
          onChange={e => setForm({ ...form, decisor: e.target.checked })}>
          ★ É decisor / contato principal
        </Checkbox>
      </Form>
    </Modal>
  );
}

// Multi-select de emails (array). Para "Equipe técnica" — vários executores.
function LinhaEditavelMultiSelect({ rotulo, valores, onSalvar }: {
  rotulo: string; valores: string[]; onSalvar: (vs: string[]) => Promise<any>;
}) {
  const [editando, setEditando] = useState(false);
  const [vs, setVs] = useState<string[]>(valores || []);
  const [salvando, setSalvando] = useState(false);
  const [usuarios, setUsuarios] = useState<UsuarioOp[]>([]);
  useEffect(() => { setVs(valores || []); }, [valores]);
  useEffect(() => { if (editando) carregarUsuariosOp().then(setUsuarios); }, [editando]);

  function toggle(email: string) {
    setVs(curr => curr.includes(email) ? curr.filter(e => e !== email) : [...curr, email]);
  }

  async function commit() {
    const original = JSON.stringify(valores || []);
    const novo = JSON.stringify(vs);
    if (original === novo) { setEditando(false); return; }
    setSalvando(true);
    try {
      await onSalvar(vs);
      setEditando(false);
    } catch { setVs(valores || []); }
    finally { setSalvando(false); }
  }

  // Mostra nomes/emails da seleção atual
  const exibido = (valores || []).map(e => {
    const u = usuarios.find(x => x.email.toLowerCase() === e.toLowerCase());
    return u ? u.nome : e.split('@')[0];
  }).join(', ');

  return (
    <div style={{ display: 'grid', gridTemplateColumns: '160px 1fr 24px', gap: 6,
      padding: '4px 0', fontSize: 12, color: '#222', alignItems: 'flex-start' }}>
      <span style={{ color: '#888', paddingTop: 2 }}>{rotulo}</span>
      {editando ? (
        <div>
          <Select mode="multiple" disabled={salvando}
            value={vs} onChange={setVs}
            placeholder="Selecione 1 ou mais usuários…"
            allowClear showSearch optionFilterProp="label"
            style={{ width: '100%' }} maxTagCount="responsive"
            options={usuarios.map(u => ({ value: u.email, label: `${u.nome} (${u.email})` }))} />
          <Space style={{ marginTop: 6, justifyContent: 'flex-end', display: 'flex' }}>
            <Button size="small" onClick={() => { setVs(valores || []); setEditando(false); }} disabled={salvando}>
              Cancelar
            </Button>
            <Button size="small" type="primary" loading={salvando} onClick={commit}>
              Salvar
            </Button>
          </Space>
        </div>
      ) : (
        <Typography.Text style={{ color: (valores || []).length > 0 ? '#222' : '#bbb' }}>
          {exibido || '— ninguém ainda'}
        </Typography.Text>
      )}
      {!editando && (
        <button onClick={() => setEditando(true)} title="Editar equipe"
          style={{ background: 'none', border: 'none', cursor: 'pointer',
            color: '#0930D0', fontSize: 11, padding: 0 }}>✏</button>
      )}
    </div>
  );
}

// Linha com valor editável inline. Tipo email — entra em modo edit ao clicar no
// ícone ✏, salva ao Enter/blur, cancela com Esc.
// (Mantida pra outros usos — agora a equipe usa dropdown.)
// eslint-disable-next-line @typescript-eslint/no-unused-vars
function LinhaEditavelEmail({ rotulo, valor, onSalvar }: {
  rotulo: string; valor?: string | null; onSalvar: (v: string | null) => Promise<any>;
}) {
  const [editando, setEditando] = useState(false);
  const [v, setV] = useState(valor || '');
  const [salvando, setSalvando] = useState(false);
  useEffect(() => { setV(valor || ''); }, [valor]);

  async function commit() {
    const novo = v.trim();
    if (novo === (valor || '')) { setEditando(false); return; }
    setSalvando(true);
    try {
      await onSalvar(novo || null);
      setEditando(false);
    } catch { setV(valor || ''); }
    finally { setSalvando(false); }
  }

  return (
    <div style={{ display: 'grid', gridTemplateColumns: '160px 1fr 24px', gap: 6,
      padding: '4px 0', fontSize: 12, color: '#222', alignItems: 'center' }}>
      <span style={{ color: '#888' }}>{rotulo}</span>
      {editando ? (
        <Input value={v} onChange={e => setV(e.target.value)} autoFocus
          disabled={salvando} size="small"
          placeholder="email@hydrodataeng.com.br"
          onPressEnter={commit}
          onKeyDown={(e) => { if (e.key === 'Escape') { setV(valor || ''); setEditando(false); } }}
          onBlur={commit} />
      ) : (
        <Typography.Text style={{ color: valor ? '#222' : '#bbb' }}>
          {valor || '— a definir'}
        </Typography.Text>
      )}
      {!editando && (
        <Button type="text" size="small" icon={<EditOutlined />}
          onClick={() => setEditando(true)} title="Editar"
          style={{ color: '#0930D0' }} />
      )}
    </div>
  );
}

// Linha com data editável (input type=date)
function LinhaEditavelData({ rotulo, valor, onSalvar }: {
  rotulo: string; valor?: string | null; onSalvar: (v: string | null) => Promise<any>;
}) {
  const [editando, setEditando] = useState(false);
  const iso = (valor ? String(valor).slice(0, 10) : '');
  const [v, setV] = useState(iso);
  const [salvando, setSalvando] = useState(false);
  useEffect(() => { setV(iso); }, [valor]); // eslint-disable-line

  async function commit() {
    if (v === iso) { setEditando(false); return; }
    setSalvando(true);
    try {
      await onSalvar(v || null);
      setEditando(false);
    } catch { setV(iso); }
    finally { setSalvando(false); }
  }

  return (
    <div style={{ display: 'grid', gridTemplateColumns: '160px 1fr 24px', gap: 6,
      padding: '4px 0', fontSize: 12, color: '#222', alignItems: 'center' }}>
      <span style={{ color: '#888' }}>{rotulo}</span>
      {editando ? (
        <DatePicker autoFocus disabled={salvando} size="small"
          value={v ? dayjs(v) : null}
          onChange={(d) => {
            const novo = d ? d.format('YYYY-MM-DD') : '';
            setV(novo);
          }}
          onOpenChange={(open) => { if (!open) commit(); }}
          format="DD/MM/YYYY" style={{ width: '100%' }} />
      ) : (
        <Typography.Text style={{ color: valor ? '#222' : '#bbb' }}>
          {valor ? fmtData(valor) : '— a definir'}
        </Typography.Text>
      )}
      {!editando && (
        <Button type="text" size="small" icon={<EditOutlined />}
          onClick={() => setEditando(true)} title="Editar"
          style={{ color: '#0930D0' }} />
      )}
    </div>
  );
}

// Painel de briefing — mostra o que tem + botão pra Dwight gerar handover
function BriefingPainel({ contrato, onAtualizar }: { contrato: Contrato; onAtualizar: () => void }) {
  const [gerando, setGerando] = useState(false);
  const [editando, setEditando] = useState<string | null>(null);
  const [valorEdit, setValorEdit] = useState('');
  const [salvando, setSalvando] = useState(false);

  const blocos: Array<[string, string | undefined, string]> = [
    ['Objetivos', contrato.objetivos as any, 'objetivos'],
    ['Requisitos específicos', contrato.requisitos_especificos as any, 'requisitos_especificos'],
    ['Restrições técnicas', contrato.restricoes_tecnicas as any, 'restricoes_tecnicas'],
    ['Dependências de terceiros', contrato.dependencias_terceiros as any, 'dependencias_terceiros'],
    ['Cronograma', contrato.cronograma_texto as any, 'cronograma_texto'],
    ['Entregas', contrato.entregas_texto as any, 'entregas_texto'],
  ];
  const preenchidos = blocos.filter(([_, v]) => v && String(v).trim().length > 0).length;
  const totalBlocos = blocos.length;

  // KICKOFF (2026-08-20): o briefing virou assíncrono. A versão síncrona tinha
  // `timeout: 90000` no axios, mas a rota passa pelo API Gateway, que corta em
  // 29 s — uma geração longa devolvia 504 pro usuário com o UPDATE às vezes já
  // aplicado no banco. Agora a chamada só dispara o job e a tela fica olhando
  // o contrato até os blocos aparecerem.
  async function pedirHandoverDwight() {
    if (!window.confirm(
      'Pedir ao Dwight pra gerar o briefing técnico (handover do comercial pro operacional)?\n\n' +
      'Ele vai ler a venda + composição + observações do negócio e preencher os blocos automaticamente.'
    )) return;
    setGerando(true);
    try {
      const token = await getToken();
      await axios.post(`${API_URL}/comercial/propostas-geradas`,
        { action: 'op_gerar_briefing_async', contrato_id: contrato.id },
        { headers: { Authorization: token } });
      // Recarrega algumas vezes até os blocos chegarem (o job leva ~20-40 s).
      // Sem estado novo no banco: quem responde é o próprio contrato.
      for (let tentativa = 0; tentativa < 8; tentativa++) {
        await new Promise(r => setTimeout(r, 8000));
        onAtualizar();
      }
    } catch (e: any) {
      alert('Erro: ' + (e.response?.data?.error || e.message));
    } finally { setGerando(false); }
  }

  function abrirEdit(campo: string, atual: string | undefined) {
    setEditando(campo);
    setValorEdit(atual || '');
  }

  async function salvarBloco() {
    if (!editando) return;
    setSalvando(true);
    try {
      const token = await getToken();
      await axios.post(`${API_URL}/comercial/propostas-geradas`,
        { action: 'op_atualizar_contrato', id: contrato.id, [editando]: valorEdit || null },
        { headers: { Authorization: token } });
      setEditando(null);
      onAtualizar();
    } catch (e: any) {
      alert('Erro: ' + (e.response?.data?.error || e.message));
    } finally { setSalvando(false); }
  }

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 10 }}>
        <div style={{ fontSize: 12, color: '#666', flex: 1 }}>
          <strong style={{ color: preenchidos === totalBlocos ? '#0a7f4a' : '#B07500' }}>
            {preenchidos}/{totalBlocos}
          </strong> blocos preenchidos. Clique no ✏ pra editar manualmente, ou peça pro Dwight gerar.
          {gerando && <div style={{ color: '#7C3AED', marginTop: 2 }}>
            O Dwight está escrevendo — os blocos aparecem sozinhos em menos de um minuto.
          </div>}
        </div>
        <Button icon={<RobotOutlined />} loading={gerando} onClick={pedirHandoverDwight}
          style={{ background: '#7C3AED', borderColor: '#7C3AED', color: '#fff' }}>
          Dwight: gerar handover
        </Button>
      </div>
      {blocos.map(([titulo, valor, campo]) => (
        <details key={campo} style={{ marginTop: 6 }} open={editando === campo}>
          <summary style={{ fontSize: 11, color: valor ? '#020D7F' : '#bbb',
            fontWeight: valor ? 600 : 400, cursor: 'pointer', padding: '4px 0',
            display: 'flex', alignItems: 'center', gap: 6 }}>
            <span>{valor ? '●' : '○'} {titulo}</span>
            {!valor && <span style={{ fontSize: 10, fontStyle: 'italic' }}>vazio</span>}
            <span style={{ flex: 1 }} />
            <button onClick={(e) => { e.preventDefault(); abrirEdit(campo, valor); }}
              title="Editar bloco"
              style={{ background: 'none', border: 'none', cursor: 'pointer',
                color: '#0930D0', fontSize: 11, padding: '0 4px' }}>✏</button>
          </summary>
          <div style={{ padding: '6px 0 10px 14px' }}>
            {editando === campo ? (
              <div>
                <textarea value={valorEdit}
                  onChange={e => setValorEdit(e.target.value)}
                  disabled={salvando}
                  placeholder="Markdown simples (listas, parágrafos)"
                  style={{ width: '100%', minHeight: 120, fontSize: 11, padding: 8,
                    borderRadius: 4, border: '0.5px solid #0930D0', fontFamily: 'inherit',
                    boxSizing: 'border-box', resize: 'vertical' }} />
                <div style={{ display: 'flex', gap: 6, marginTop: 6, justifyContent: 'flex-end' }}>
                  <Button size="small" onClick={() => setEditando(null)} disabled={salvando}>Cancelar</Button>
                  <Button size="small" type="primary" loading={salvando} onClick={salvarBloco}>Salvar</Button>
                </div>
              </div>
            ) : (
              <div style={{ fontSize: 11, color: '#444', whiteSpace: 'pre-wrap', lineHeight: 1.5 }}>
                {valor || <em style={{ color: '#bbb' }}>— vazio</em>}
              </div>
            )}
          </div>
        </details>
      ))}
    </div>
  );
}
